import React, { useState, useEffect } from 'react';
import { RefreshCw, CheckCircle, XCircle, Clock, AlertCircle, Search } from 'lucide-react';
import WhatsAppIcon from '../components/icons/WhatsAppIcon';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import {
  getMessageHistory,
  retryMessage,
  bulkRetryMessages,
  getMessageStatusColor,
  getMessageStatusIcon,
  getMessageStatusLabel,
  formatPhoneNumber,
  formatTimestamp,
  getTimeSince,
  canRetryMessage,
  getRetryCountDisplay,
  getNetworkProvider
} from '../api/whatsapp';

/**
 * WhatsApp Message History Page
 * Displays all WhatsApp messages with filters, pagination, and retry functionality
 */
const WhatsAppHistoryPage = () => {
  const [messages, setMessages] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retryingIds, setRetryingIds] = useState(new Set());
  const [bulkRetrying, setBulkRetrying] = useState(false);
  const [selectedMessages, setSelectedMessages] = useState(new Set());
  const [filters, setFilters] = useState({
    status: '',
    phone: '',
    startDate: '',
    endDate: '',
    saleId: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const { user } = useAuth();
  const limit = 20;

  useEffect(() => {
    fetchMessages();
  }, [filters, currentPage]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      setError('');

      const params = {
        page: currentPage,
        limit,
        status: filters.status || undefined,
        phone: filters.phone || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        saleId: filters.saleId || undefined
      };

      // Remove undefined params
      Object.keys(params).forEach(key => {
        if (params[key] === undefined) delete params[key];
      });

      const response = await getMessageHistory(params);
      setMessages(response.data || []);
      setTotal(response.pagination?.total || 0);
    } catch (err) {
      console.error('Failed to fetch messages:', err);
      setError(err.response?.data?.message || 'Failed to fetch WhatsApp messages');
      toast.error('Failed to fetch WhatsApp messages');
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = async (messageId) => {
    if (retryingIds.has(messageId)) return;

    setRetryingIds(prev => new Set(prev).add(messageId));

    try {
      await retryMessage(messageId);
      toast.success('Message retry initiated successfully');

      // Refresh message list after a short delay
      setTimeout(() => {
        fetchMessages();
      }, 1000);
    } catch (err) {
      console.error('Retry failed:', err);
      toast.error(err.response?.data?.message || 'Failed to retry message');
    } finally {
      setRetryingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(messageId);
        return newSet;
      });
    }
  };

  const handleBulkRetry = async () => {
    if (selectedMessages.size === 0) {
      toast.warning('Please select messages to retry');
      return;
    }

    const failedMessages = Array.from(selectedMessages).filter(id => {
      const message = messages.find(m => m.id === id);
      return message && canRetryMessage(message);
    });

    if (failedMessages.length === 0) {
      toast.warning('No eligible messages selected for retry');
      return;
    }

    setBulkRetrying(true);

    try {
      await bulkRetryMessages(failedMessages);
      toast.success(`Successfully initiated retry for ${failedMessages.length} message(s)`);
      setSelectedMessages(new Set());

      // Refresh message list after a short delay
      setTimeout(() => {
        fetchMessages();
      }, 1000);
    } catch (err) {
      console.error('Bulk retry failed:', err);
      toast.error(err.response?.data?.message || 'Failed to retry messages');
    } finally {
      setBulkRetrying(false);
    }
  };

  const handleSelectMessage = (messageId) => {
    setSelectedMessages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedMessages.size === messages.length) {
      setSelectedMessages(new Set());
    } else {
      setSelectedMessages(new Set(messages.map(m => m.id)));
    }
  };

  const getStatusBadge = (status) => {
    const color = getMessageStatusColor(status);
    const icon = getMessageStatusIcon(status);
    const label = getMessageStatusLabel(status);

    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded"
        style={{ backgroundColor: `${color}20`, color }}
      >
        <span>{icon}</span>
        {label}
      </span>
    );
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent':
      case 'delivered':
      case 'read':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-500" />;
    }
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'system_admin';
  const totalPages = Math.ceil(total / limit);
  const hasSelectedMessages = selectedMessages.size > 0;
  const eligibleForRetry = Array.from(selectedMessages).filter(id => {
    const message = messages.find(m => m.id === id);
    return message && canRetryMessage(message);
  }).length;

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <div className="flex items-center gap-2">
            <WhatsAppIcon className="w-6 h-6 text-accent-gold" />
            <h1 className="text-2xl font-bold text-primary-black">WhatsApp Message History</h1>
          </div>
          <p className="text-sm text-secondary-gray mt-1">
            Track and manage WhatsApp receipt deliveries
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchMessages}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 border border-secondary-lightGray rounded-lg text-secondary-gray hover:bg-secondary-offWhite transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          {hasSelectedMessages && (
            <button
              onClick={handleBulkRetry}
              disabled={bulkRetrying || eligibleForRetry === 0}
              className="flex items-center gap-2 px-4 py-2 bg-accent-gold text-white rounded-lg hover:bg-accent-gold/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw className={`w-4 h-4 ${bulkRetrying ? 'animate-spin' : ''}`} />
              Retry Selected ({eligibleForRetry})
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-secondary-lightGray p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-primary-black mb-1">
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => {
                setFilters({ ...filters, status: e.target.value });
                setCurrentPage(1);
              }}
              className="w-full border border-secondary-lightGray rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent-gold"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="sent">Sent</option>
              <option value="delivered">Delivered</option>
              <option value="read">Read</option>
              <option value="failed">Failed</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-primary-black mb-1">
              Phone Number
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-gray" />
              <input
                type="text"
                value={filters.phone}
                onChange={(e) => {
                  setFilters({ ...filters, phone: e.target.value });
                  setCurrentPage(1);
                }}
                placeholder="Search phone..."
                className="w-full pl-10 pr-3 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-primary-black mb-1">
              From Date
            </label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => {
                setFilters({ ...filters, startDate: e.target.value });
                setCurrentPage(1);
              }}
              className="w-full border border-secondary-lightGray rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent-gold"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-primary-black mb-1">
              To Date
            </label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => {
                setFilters({ ...filters, endDate: e.target.value });
                setCurrentPage(1);
              }}
              className="w-full border border-secondary-lightGray rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent-gold"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-primary-black mb-1">
              Receipt #
            </label>
            <input
              type="text"
              value={filters.saleId}
              onChange={(e) => {
                setFilters({ ...filters, saleId: e.target.value });
                setCurrentPage(1);
              }}
              placeholder="Search receipt..."
              className="w-full border border-secondary-lightGray rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent-gold"
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          {error}
        </div>
      )}

      {/* Messages Table */}
      <div className="bg-white rounded-lg shadow-sm border border-secondary-lightGray overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-gold"></div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-secondary-lightGray">
                <thead className="bg-secondary-offWhite">
                  <tr>
                    <th className="px-6 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedMessages.size === messages.length && messages.length > 0}
                        onChange={handleSelectAll}
                        className="h-4 w-4 text-accent-gold rounded border-secondary-lightGray focus:ring-accent-gold"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-gray uppercase tracking-wider">
                      Receipt #
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-gray uppercase tracking-wider">
                      Phone Number
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-gray uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-gray uppercase tracking-wider">
                      Sent At
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-secondary-gray uppercase tracking-wider">
                      Retries
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-secondary-gray uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-secondary-lightGray">
                  {messages.length > 0 ? (
                    messages.map((message) => {
                      const isRetrying = retryingIds.has(message.id);
                      const isSelected = selectedMessages.has(message.id);
                      const canRetry = canRetryMessage(message);
                      const provider = getNetworkProvider(message.recipient_phone);

                      return (
                        <tr
                          key={message.id}
                          className={`hover:bg-secondary-offWhite transition-colors ${
                            isSelected ? 'bg-blue-50' : ''
                          }`}
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleSelectMessage(message.id)}
                              className="h-4 w-4 text-accent-gold rounded border-secondary-lightGray focus:ring-accent-gold"
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-accent-gold">
                            {message.receipt_number}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-primary-black">
                            <div className="font-medium">
                              {formatPhoneNumber(message.recipient_phone)}
                            </div>
                            {provider && provider !== 'Unknown' && (
                              <div className="text-xs text-secondary-gray">{provider}</div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getStatusBadge(message.message_status)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-gray">
                            <div>{formatTimestamp(message.sent_at || message.created_at)}</div>
                            <div className="text-xs text-secondary-gray">
                              {getTimeSince(message.sent_at || message.created_at)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-secondary-gray">
                            {getRetryCountDisplay(message)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            {canRetry ? (
                              <button
                                onClick={() => handleRetry(message.id)}
                                disabled={isRetrying}
                                className="inline-flex items-center gap-1 text-accent-gold hover:text-accent-gold/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <RefreshCw className={`w-4 h-4 ${isRetrying ? 'animate-spin' : ''}`} />
                                {isRetrying ? 'Retrying...' : 'Retry'}
                              </button>
                            ) : message.message_status === 'failed' ? (
                              <span className="text-xs text-red-500">Max retries reached</span>
                            ) : (
                              <span className="text-xs text-secondary-gray">-</span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="7" className="px-6 py-12 text-center">
                        <WhatsAppIcon className="w-12 h-12 text-secondary-gray mx-auto mb-2" />
                        <p className="text-secondary-gray">No WhatsApp messages found</p>
                        <p className="text-sm text-secondary-gray mt-1">
                          Messages will appear here after sending receipts via WhatsApp
                        </p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-secondary-lightGray">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-secondary-lightGray text-sm font-medium rounded-md text-secondary-gray bg-white hover:bg-secondary-offWhite disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-secondary-lightGray text-sm font-medium rounded-md text-secondary-gray bg-white hover:bg-secondary-offWhite disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-secondary-gray">
                      Showing <span className="font-medium text-primary-black">{(currentPage - 1) * limit + 1}</span> to{' '}
                      <span className="font-medium text-primary-black">{Math.min(currentPage * limit, total)}</span> of{' '}
                      <span className="font-medium text-primary-black">{total}</span> results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-secondary-lightGray bg-white text-sm font-medium text-secondary-gray hover:bg-secondary-offWhite disabled:opacity-50"
                      >
                        Previous
                      </button>
                      <span className="relative inline-flex items-center px-4 py-2 border border-secondary-lightGray bg-white text-sm font-medium text-primary-black">
                        Page {currentPage} of {totalPages}
                      </span>
                      <button
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-secondary-lightGray bg-white text-sm font-medium text-secondary-gray hover:bg-secondary-offWhite disabled:opacity-50"
                      >
                        Next
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Summary Cards */}
      {messages.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mt-6">
          <div className="bg-white rounded-lg shadow-sm border border-secondary-lightGray p-4">
            <div className="text-sm text-secondary-gray">Total Messages</div>
            <div className="text-2xl font-bold text-primary-black">{total}</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-secondary-lightGray p-4">
            <div className="text-sm text-secondary-gray">Pending</div>
            <div className="text-2xl font-bold text-yellow-600">
              {messages.filter(m => m.message_status === 'pending').length}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-secondary-lightGray p-4">
            <div className="text-sm text-secondary-gray">Delivered</div>
            <div className="text-2xl font-bold text-green-600">
              {messages.filter(m => m.message_status === 'delivered' || m.message_status === 'read').length}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-secondary-lightGray p-4">
            <div className="text-sm text-secondary-gray">Failed</div>
            <div className="text-2xl font-bold text-red-600">
              {messages.filter(m => m.message_status === 'failed').length}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-secondary-lightGray p-4">
            <div className="text-sm text-secondary-gray">Success Rate</div>
            <div className="text-2xl font-bold text-accent-gold">
              {total > 0
                ? Math.round(
                    (messages.filter(m => m.message_status === 'delivered' || m.message_status === 'read').length /
                      total) *
                      100
                  )
                : 0}
              %
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatsAppHistoryPage;
